// SHARED-DES (docs/design/2026-10-30-shared-des-and-fulfillment.md §2.2, §3): one DE, one home
// seat, `serves` links to other teams and seats.
//   - the chart's linked card under a served lead (dashed, muted, "Shared · <home team>", no
//     numbers: they stay on the home card), and the "Shared" badge on the home card;
//   - the seat drawer's Serves line: chips per link with a grants popover (talk, task, see) and
//     Remove, "+ Add team or seat" (a small picker), a local draft, Review (`PUT … dry_run`, the
//     `say` lines) then Apply. Owners and admins edit; everyone else reads.

import { ApiError, type OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InfoTip,
  Input,
  Notice,
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
  Switch,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, XIcon } from "lucide-react";
import { type DragEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { FormError } from "../structure/parts";
import type { ChartSeat, OrgChart } from "./chart-model";
import { SeatAvatar } from "./chart-parts";
import { chartKeys } from "./chart-queries";

export const SHARED_HINT = "One DE, one memory, one KPI set. Numbers show on the home card.";
export const LINKED_DRAG_REFUSAL = "Shared DEs are linked, not moved · edit Serves in the drawer";

type ServesView = OperationOutput<"seats.serves">;
type ServesResult = OperationOutput<"seats.servesUpdate">;
type Grants = ServesView["links"][number]["grants"];
type Kind = ServesView["links"][number]["target_kind"];
type DraftLink = { target_kind: Kind; target_id: string | null; name: string; grants: Grants };

const ALL: Grants = { talk: true, task: true, see: true };
const GRANTS: { key: keyof Grants; label: string }[] = [
  { key: "talk", label: "Talk" },
  { key: "task", label: "Task" },
  { key: "see", label: "See" },
];
const keyOf = (l: { target_kind: string; target_id: string | null }) =>
  `${l.target_kind}:${l.target_id ?? ""}`;

export const servesKey = (co: string, seatId: string) =>
  [...chartKeys.orgChart(co), "serves", seatId] as const;

/**
 * The home team's name: the team the home seat is a member of (else the one it leads), else the
 * home seat's supervisor's title.
 */
export function homeTeamName(chart: OrgChart | undefined, homeSeatId: string): string {
  if (!chart) return "Home";
  const member = chart.team_members.find((m) => m.seat_id === homeSeatId);
  const team =
    chart.teams.find((t) => t.id === member?.team_id) ??
    chart.teams.find((t) => t.lead_seat_id === homeSeatId);
  if (team) return team.name;
  const home = chart.seats.find((s) => s.id === homeSeatId);
  return chart.seats.find((s) => s.id === home?.supervisor_seat_id)?.title ?? "Home";
}

/** The home seats of shared DEs (a seat with at least one serves link). */
export const sharedSeatIds = (chart: OrgChart | undefined): Set<string> =>
  new Set((chart?.service_links ?? []).map((l) => l.seat_id));

/** "Shared · Fulfillment" (or "Shared" on the home card), with the (i) that says what it means. */
export function SharedBadge({ home, className }: { home?: string; className?: string }) {
  return (
    <span
      className={cn("inline-flex min-w-0 items-center gap-0.5", className)}
      data-slot="shared-badge"
    >
      <Badge variant="outline" className="min-w-0 max-w-36 border-dashed">
        <span className="truncate">{home ? `Shared · ${home}` : "Shared"}</span>
      </Badge>
      <InfoTip label="About shared DEs" className="size-4">
        {SHARED_HINT}
      </InfoTip>
    </span>
  );
}

/** The DE's name on a seat (its DE holder), else the seat's title. */
export const deNameOf = (seat: Pick<ChartSeat, "holders" | "title">) =>
  seat.holders.find((h) => h.membership_kind === "digital_employee")?.display_name ?? seat.title;

/**
 * A shared DE's card under a served lead: same size as a seat card, dashed and muted, the DE and
 * its home team; no numbers, agents or grip. A click opens the home seat's drawer; a drag is
 * refused (the link is edited in the drawer); a seat dropped on it targets the home seat.
 */
export function LinkedCard({
  home,
  homeTeam,
  linkId,
  manager,
  selected,
  onOpen,
  onRefuseDrag,
  drop,
}: {
  home: ChartSeat;
  homeTeam: string;
  linkId: string;
  manager: boolean;
  selected: boolean;
  onOpen: () => void;
  onRefuseDrag: () => void;
  drop?: {
    onDragOver?: (e: DragEvent) => void;
    onDragLeave?: () => void;
    onDrop?: (e: DragEvent) => void;
  };
}) {
  const name = deNameOf(home);
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the name button is the keyboard way; the card click is a shortcut.
    <article
      aria-label={`${name} (shared from ${homeTeam})`}
      data-slot="linked-card"
      data-testid="linked-card"
      data-home-seat={home.id}
      data-link={linkId}
      data-panel={selected ? "" : undefined}
      draggable={manager}
      onDragStart={(e) => {
        e.preventDefault();
        onRefuseDrag();
      }}
      {...drop}
      onClick={(e) => {
        if ((e.target as Element).closest("[data-slot='info-tip']")) return;
        onOpen();
      }}
      className="group/card relative flex min-h-[68px] w-[184px] min-w-0 cursor-pointer flex-col gap-1 rounded-lg border-[1.5px] border-dashed border-strong bg-sunken px-2 pt-2 pb-1.5 transition-colors duration-(--dur-fast) hover:border-primary data-panel:ring-2 data-panel:ring-primary/40"
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <SeatAvatar seat={home} size="xs" />
        <button
          type="button"
          className="min-w-0 truncate rounded-sm text-left text-xs leading-4 font-semibold text-muted-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring"
          aria-label={`Open ${name}'s home seat`}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          {name}
        </button>
      </div>
      <span className="truncate text-2xs text-faint" title={home.title}>
        {home.title}
      </span>
      <SharedBadge home={homeTeam} />
    </article>
  );
}

// The drawer's Serves line -----------------------------------------------------------------------

// SHARED-DES-2: a team the reader may not see comes without its name (the chart's team rule).
const draftOf = (v: ServesView): DraftLink[] =>
  v.links.map((l) => ({
    target_kind: l.target_kind,
    target_id: l.target_id,
    name:
      l.target_kind === "company"
        ? "Company"
        : (l.target_name ?? (l.target_kind === "team" && !v.editable ? "A private team" : "Gone")),
    grants: l.grants,
  }));

const sameLinks = (a: readonly DraftLink[], b: readonly DraftLink[]) =>
  a.length === b.length &&
  a.every((x) => {
    const y = b.find((z) => keyOf(z) === keyOf(x));
    return (
      y &&
      y.grants.talk === x.grants.talk &&
      y.grants.task === x.grants.task &&
      y.grants.see === x.grants.see
    );
  });

const grantText = (g: Grants) => {
  const on = GRANTS.filter((x) => g[x.key]).map((x) => x.label.toLowerCase());
  return on.length ? on.join(", ") : "no access";
};

/** The seat drawer's "Serves" line for a seat a DE holds. */
export function ServesLine({
  api,
  company,
  seatId,
  chart,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  chart: OrgChart | undefined;
}) {
  const qc = useQueryClient();
  const co = company.id;
  const q = useQuery({
    queryKey: servesKey(co, seatId),
    queryFn: () => api.client.seats.serves({ params: { co, id: seatId } }),
  });
  const [draft, setDraft] = useState<DraftLink[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [review, setReview] = useState<ServesResult | null>(null);
  const stored = q.data ? draftOf(q.data) : [];
  const links = draft ?? stored;
  const dirty = draft !== null && !sameLinks(draft, stored);
  const editable = q.data?.editable === true;
  const body = (dry: boolean) => ({
    links: links.map((l) => ({
      target_kind: l.target_kind,
      target_id: l.target_id,
      grants: l.grants,
    })),
    ...(dry ? { dry_run: true } : {}),
  });
  // SHARED-DES-2: every write goes under the version this drawer read; a set someone changed
  // since is a 409 — the drawer drops its draft and shows the fresh links, never overwriting.
  // The version the draft started from (a background refetch must not move it under the draft).
  const [draftVersion, setDraftVersion] = useState<number | null>(null);
  const ifMatch = (draft !== null ? draftVersion : null) ?? q.data?.version ?? 0;
  const stale = (e: unknown) => {
    if (!(e instanceof ApiError && e.status === 409)) return;
    setDraft(null);
    setReview(null);
    toast.add({
      title: "Serves changed meanwhile",
      description: "Someone saved first. Here are the current links.",
      type: "error",
    });
    void qc.invalidateQueries({ queryKey: servesKey(co, seatId) });
  };
  const check = useMutation({
    mutationFn: () =>
      api.client.seats.servesUpdate({ params: { co, id: seatId }, body: body(true), ifMatch }),
    onSuccess: (r) => setReview(r),
    onError: stale,
  });
  const apply = useMutation({
    mutationFn: () =>
      api.client.seats.servesUpdate({ params: { co, id: seatId }, body: body(false), ifMatch }),
    onError: stale,
    onSuccess: (r) => {
      const { changes: _c, applied: _a, ...view } = r;
      qc.setQueryData(servesKey(co, seatId), view);
      setDraft(null);
      setReview(null);
      toast.add({ title: "Serves saved", type: "success" });
      void qc.invalidateQueries({ queryKey: chartKeys.orgChart(co) });
    },
  });
  const edit = (f: (cur: DraftLink[]) => DraftLink[]) => {
    if (draft === null) setDraftVersion(q.data?.version ?? null);
    setDraft(f(links));
  };
  if (q.isPending || q.isError) return null;

  return (
    <div className="mt-1.5 flex flex-col gap-1" data-slot="seat-serves">
      <span className="flex flex-wrap items-center gap-1 text-xs">
        <span className="text-faint">Serves</span>
        {links.length === 0 ? (
          <span className="text-faint" data-slot="serves-empty">
            Not shared
          </span>
        ) : null}
        {links.map((l) => (
          <ServesChip
            key={keyOf(l)}
            link={l}
            editable={editable}
            onGrants={(g) =>
              edit((cur) => cur.map((x) => (keyOf(x) === keyOf(l) ? { ...x, grants: g } : x)))
            }
            onRemove={() => edit((cur) => cur.filter((x) => keyOf(x) !== keyOf(l)))}
          />
        ))}
        {editable ? (
          <Button size="xs" variant="ghost" data-slot="serves-add" onClick={() => setAdding(true)}>
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            Add team or seat
          </Button>
        ) : null}
        <InfoTip label="About Serves">
          The teams and seats this DE also works for. Its home, KPIs and budget stay on this seat.
        </InfoTip>
      </span>
      {dirty ? (
        <span className="flex items-center gap-1">
          <Button
            size="xs"
            data-slot="serves-review"
            disabled={check.isPending}
            onClick={() => check.mutate()}
          >
            Review
          </Button>
          <Button size="xs" variant="ghost" onClick={() => setDraft(null)}>
            Discard
          </Button>
        </span>
      ) : null}
      <FormError what="Checking the change" error={check.error} />
      {adding && chart ? (
        <AddServesDialog
          chart={chart}
          homeSeatId={seatId}
          taken={new Set(links.map(keyOf))}
          onPick={(l) => {
            edit((cur) => [...cur, l]);
            setAdding(false);
          }}
          onClose={() => setAdding(false)}
        />
      ) : null}
      {review ? (
        <Dialog open onOpenChange={(open) => !open && setReview(null)}>
          <DialogContent className="sm:max-w-[520px]" data-slot="serves-review-dialog">
            <DialogHeader>
              <DialogTitle>Review Serves</DialogTitle>
              <DialogDescription>Apply writes these changes and tells the DE.</DialogDescription>
            </DialogHeader>
            {review.changes.length ? (
              <ul className="flex flex-col gap-1.5 text-sm" data-slot="serves-changes">
                {review.changes.map((c) => (
                  <li key={`${c.op}:${keyOf(c)}`} data-op={c.op} className="flex gap-2">
                    <Badge
                      variant={c.op === "remove" ? "danger" : c.op === "add" ? "success" : "info"}
                    >
                      {c.op === "add" ? "Add" : c.op === "remove" ? "Remove" : "Grants"}
                    </Badge>
                    <span className="min-w-0">{c.say}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <Notice tone="info" title="Nothing changes" />
            )}
            <FormError what="Saving Serves" error={apply.error} />
            <DialogFooter>
              <Button variant="secondary" onClick={() => setReview(null)}>
                Cancel
              </Button>
              <Button
                disabled={apply.isPending || review.changes.length === 0}
                onClick={() => apply.mutate()}
              >
                Apply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function ServesChip({
  link,
  editable,
  onGrants,
  onRemove,
}: {
  link: DraftLink;
  editable: boolean;
  onGrants: (g: Grants) => void;
  onRemove: () => void;
}) {
  const partial = !link.grants.talk || !link.grants.task || !link.grants.see;
  const label = (
    <>
      <span className="truncate">{link.name}</span>
      {partial ? <span className="text-faint">· {grantText(link.grants)}</span> : null}
    </>
  );
  if (!editable)
    return (
      <span
        data-slot="serves-chip"
        data-target={link.name}
        title={`${link.name}: ${grantText(link.grants)}`}
        className="inline-flex max-w-48 min-w-0 items-center gap-1 rounded-md border border-border bg-card px-1.5 py-0.5"
      >
        {label}
      </span>
    );
  return (
    <span
      data-slot="serves-chip"
      data-target={link.name}
      className="group/chip inline-flex max-w-56 min-w-0 items-center rounded-md border border-border bg-card"
    >
      <Popover>
        <PopoverTrigger
          render={
            <button
              type="button"
              aria-label={`${link.name}: ${grantText(link.grants)}`}
              className="inline-flex min-w-0 items-center gap-1 rounded-l-md py-0.5 pl-1.5 hover:bg-sunken focus-visible:outline-2 focus-visible:outline-ring"
            />
          }
        >
          {label}
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 gap-2 p-3" data-slot="serves-grants">
          <PopoverTitle className="text-sm">{link.name}</PopoverTitle>
          {GRANTS.map((g) => (
            <span key={g.key} className="flex items-center justify-between gap-2 text-sm">
              {g.label}
              <Switch
                size="sm"
                aria-label={`${g.label}: ${link.name}`}
                checked={link.grants[g.key]}
                onCheckedChange={(v) => onGrants({ ...link.grants, [g.key]: v })}
              />
            </span>
          ))}
          <Button variant="outline" size="xs" className="self-start" onClick={onRemove}>
            Remove
          </Button>
        </PopoverContent>
      </Popover>
      <button
        type="button"
        aria-label={`Remove ${link.name}`}
        onClick={onRemove}
        className="inline-flex size-5 shrink-0 items-center justify-center rounded-r-md text-faint opacity-0 hover:bg-active hover:text-foreground focus-visible:opacity-100 group-hover/chip:opacity-100 pointer-coarse:opacity-100"
      >
        <XIcon aria-hidden="true" className="size-3" />
      </button>
    </span>
  );
}

/** "+ Add team or seat": the company, then teams, then seats (not this one, not taken). */
function AddServesDialog({
  chart,
  homeSeatId,
  taken,
  onPick,
  onClose,
}: {
  chart: OrgChart;
  homeSeatId: string;
  taken: ReadonlySet<string>;
  onPick: (l: DraftLink) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState("");
  const match = (name: string) => name.toLowerCase().includes(text.trim().toLowerCase());
  const options: { title: string; items: DraftLink[] }[] = [
    {
      title: "Company",
      items: [{ target_kind: "company" as const, target_id: null, name: "Company", grants: ALL }],
    },
    {
      title: "Teams",
      items: [...chart.teams]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({ target_kind: "team" as const, target_id: t.id, name: t.name, grants: ALL })),
    },
    {
      title: "Seats",
      items: chart.seats
        .filter((s) => s.id !== homeSeatId && s.status !== "closed")
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((s) => ({
          target_kind: "seat" as const,
          target_id: s.id,
          name: s.title,
          grants: ALL,
        })),
    },
  ]
    .map((g) => ({ ...g, items: g.items.filter((l) => !taken.has(keyOf(l)) && match(l.name)) }))
    .filter((g) => g.items.length > 0);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[420px]" data-slot="serves-add-dialog">
        <DialogHeader>
          <DialogTitle>Add team or seat</DialogTitle>
        </DialogHeader>
        <Input
          autoFocus
          aria-label="Find a team or seat"
          placeholder="Find a team or seat"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="flex max-h-72 flex-col gap-2 overflow-y-auto">
          {options.length === 0 ? <p className="text-sm text-faint">Nothing to add.</p> : null}
          {options.map((g) => (
            <div key={g.title} className="flex flex-col gap-0.5">
              <span className="px-1 text-2xs font-medium tracking-wide text-faint uppercase">
                {g.title}
              </span>
              {g.items.map((l) => (
                <button
                  key={keyOf(l)}
                  type="button"
                  className="rounded-sm px-2 py-1 text-left text-sm hover:bg-sunken focus-visible:outline-2 focus-visible:outline-ring"
                  onClick={() => onPick(l)}
                >
                  {l.name}
                </button>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
